import { 
  ArrowRight, Camera, Clock, Crown, Film, Lock, Mic, 
  Music, Sparkles, Sun, User, Video 
} from "lucide-react";
import { CopyButton } from "../ui/CopyButton";
import { DetailRow, DetailSection } from "../ui/DetailRow";
import { 
  getSegmentColors, 
  DEFAULT_SEGMENT_COLOR 
} from "../../constants/scriptConstants";

/**
 * SegmentCard Component
 * =====================
 * Displays a single script segment with all its details.
 */
export const SegmentCard = ({ 
  segment, 
  platform, 
  index, 
  isBlurred = false, 
  onUnlock 
}) => {
  const colorMap = getSegmentColors(platform);
  const colors = colorMap[segment.id] || DEFAULT_SEGMENT_COLOR;
  const visual = segment.visual || {};
  const audio = segment.audio || {};
  const textStyle = segment.text_style || {};

  // Blur details for guests (segments after index 1)
  const shouldBlur = isBlurred && index > 1;

  return (
    <div 
      className={`${colors.bg} ${colors.border} border rounded-xl overflow-hidden animate-fade-in-up relative`}
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`segment-card-${index}`}
    >
      {/* Blur Overlay for guest users */}
      {shouldBlur && (
        <div className="absolute inset-0 bg-zinc-900/80 backdrop-blur-md z-10 flex flex-col items-center justify-center gap-3">
          <Lock className="w-6 h-6 text-zinc-400" />
          <p className="text-sm text-zinc-400 font-medium">Sign up to see full details</p>
          <button
            onClick={onUnlock}
            className="px-4 py-1.5 bg-white text-black text-sm font-semibold rounded-lg hover:bg-zinc-100 transition-all"
            data-testid="unlock-btn"
          >
            Unlock Free
          </button>
        </div>
      )}

      {/* Header */}
      <div className={`px-4 py-3 border-b ${colors.border} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
          <span className={`text-xs font-bold uppercase tracking-wider ${colors.text}`}>
            {segment.name}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-zinc-500">
          <Clock className="w-3 h-3" />
          <span className="text-xs font-mono">{segment.time_range}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Caption - Main focus */}
        {segment.caption && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Caption</span>
              <CopyButton text={segment.caption} />
            </div>
            <div className="bg-black/50 rounded-xl px-5 py-4">
              <p className="text-xl md:text-2xl font-black text-white tracking-tight leading-tight">
                {segment.caption}
              </p>
            </div>
          </div>
        )}

        {/* Voice & Audio Row */}
        {(segment.voiceover || segment.voice_direction) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {segment.voiceover && (
              <div className="bg-black/30 rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-1.5">
                  <Mic className="w-3 h-3 text-zinc-500" />
                  <span className="text-[10px] font-semibold text-zinc-500 uppercase">Voiceover</span>
                </div>
                <p className="text-sm text-zinc-300 font-medium">&ldquo;{segment.voiceover}&rdquo;</p>
                {segment.voice_direction && (
                  <p className="text-xs text-zinc-500 italic">{segment.voice_direction}</p>
                )}
              </div>
            )}
            {(audio.sound_effect || audio.music_mood) && (
              <div className="bg-black/30 rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-1.5">
                  <Music className="w-3 h-3 text-zinc-500" />
                  <span className="text-[10px] font-semibold text-zinc-500 uppercase">Audio</span>
                </div>
                {audio.sound_effect && (
                  <p className="text-sm text-zinc-300"><span className="text-zinc-500">SFX:</span> {audio.sound_effect}</p>
                )}
                {audio.music_mood && (
                  <p className="text-sm text-zinc-300"><span className="text-zinc-500">Music:</span> {audio.music_mood}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Visual Details */}
        {(visual.camera_angle || visual.camera_movement || visual.lighting || visual.subject) && (
          <DetailSection title="Visual Direction">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <DetailRow icon={Camera} label="Camera Angle" value={visual.camera_angle} />
              <DetailRow icon={Video} label="Movement" value={visual.camera_movement} />
              <DetailRow icon={Sun} label="Lighting" value={visual.lighting} />
              <DetailRow icon={User} label="Subject" value={visual.subject} />
            </div>
            {visual.broll_idea && (
              <DetailRow icon={Film} label="B-Roll Option" value={visual.broll_idea} />
            )}
          </DetailSection>
        )}

        {/* Text Style */}
        {(textStyle.animation || textStyle.position || textStyle.emphasis) && (
          <DetailSection title="Caption Style">
            <div className="grid grid-cols-3 gap-2">
              {textStyle.animation && (
                <div className="bg-black/30 rounded-lg p-2 text-center">
                  <span className="text-[10px] text-zinc-500 block">Animation</span>
                  <span className="text-xs text-zinc-300 font-medium">{textStyle.animation}</span>
                </div>
              )}
              {textStyle.position && (
                <div className="bg-black/30 rounded-lg p-2 text-center">
                  <span className="text-[10px] text-zinc-500 block">Position</span>
                  <span className="text-xs text-zinc-300 font-medium">{textStyle.position}</span>
                </div>
              )}
              {textStyle.emphasis && (
                <div className="bg-black/30 rounded-lg p-2 text-center">
                  <span className="text-[10px] text-zinc-500 block">Emphasize</span>
                  <span className="text-xs text-zinc-300 font-medium">{textStyle.emphasis}</span>
                </div>
              )}
            </div>
          </DetailSection>
        )}

        {/* Transition */}
        {segment.transition && (
          <div className="flex items-center gap-2 pt-2 border-t border-zinc-800/50">
            <ArrowRight className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-[10px] font-semibold text-zinc-500 uppercase">Transition:</span>
            <span className="text-xs text-zinc-400">{segment.transition}</span>
          </div>
        )}

        {/* AI Tool Prompts - Unlimited Users Only */}
        {segment.ai_prompts && (segment.ai_prompts.video_prompts || segment.ai_prompts.image_prompts) && (
          <div className="mt-4 pt-4 border-t border-amber-500/20 space-y-3">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">AI Tool Prompts</span>
            </div>
            
            {/* Video Prompts */}
            {segment.ai_prompts.video_prompts && Object.keys(segment.ai_prompts.video_prompts).length > 0 && (
              <div className="space-y-2">
                <span className="text-[10px] font-semibold text-violet-400 uppercase flex items-center gap-1.5">
                  <Video className="w-3 h-3" /> Video Generation
                </span>
                {Object.entries(segment.ai_prompts.video_prompts).map(([toolId, prompt]) => (
                  <div key={toolId} className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-violet-300 capitalize">{toolId}</span>
                      <CopyButton text={prompt} />
                    </div>
                    <p className="text-xs text-zinc-400 leading-relaxed font-mono">{prompt}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Image Prompts */}
            {segment.ai_prompts.image_prompts && Object.keys(segment.ai_prompts.image_prompts).length > 0 && (
              <div className="space-y-2">
                <span className="text-[10px] font-semibold text-emerald-400 uppercase flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Image Generation
                </span>
                {Object.entries(segment.ai_prompts.image_prompts).map(([toolId, prompt]) => (
                  <div key={toolId} className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-emerald-300 capitalize">{toolId}</span>
                      <CopyButton text={prompt} />
                    </div>
                    <p className="text-xs text-zinc-400 leading-relaxed font-mono">{prompt}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SegmentCard;
